/**
 * 권위 서버(villager-sim) 접속 클라이언트.
 * 클라는 상태를 바꾸지 않는다 — 인텐트만 보내고 스냅샷을 받아 그린다 (지시서 §2).
 */
import { WS_PATH } from '@villager/shared';
import type {
  ClientMsg,
  EntityView,
  FeedItem,
  Intent,
  ParcelView,
  PlayerView,
  ServerMsg,
  WorldView,
} from '@villager/shared';

export interface Snapshot {
  tick: number;
  you: PlayerView;
  world: WorldView;
  ents: EntityView[];
  parcels: ParcelView[];
}

/** 개발 중에는 로컬 sim-server(:8008), 배포에서는 nginx 경유 wss. */
export function defaultWsUrl(): string {
  const { protocol, hostname, port } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // vite dev 서버(3007)에서 띄운 경우 sim-server는 8008
    if (port !== '8008') return `ws://${hostname}:8008`;
  }
  const scheme = protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${window.location.host}${WS_PATH}`;
}

export class NetClient {
  private ws: WebSocket | null = null;
  playerId = '';
  seed = 0;
  connected = false;

  /** 연결 후 main이 채워 넣는 핸들러들 */
  onSnapshot: ((s: Snapshot) => void) | null = null;
  onFeed: ((msgs: FeedItem[]) => void) | null = null;
  onSeason: ((result: unknown) => void) | null = null;
  onClose: (() => void) | null = null;

  constructor(private url: string) {}

  /** welcome 을 받으면 true. 타임아웃/실패 시 false (호출측이 싱글로 폴백). */
  connect(timeoutMs = 4000): Promise<boolean> {
    return new Promise((resolve) => {
      let settled = false;
      const done = (ok: boolean): void => {
        if (settled) return;
        settled = true;
        resolve(ok);
      };
      let ws: WebSocket;
      try {
        ws = new WebSocket(this.url);
      } catch {
        done(false);
        return;
      }
      this.ws = ws;
      const timer = setTimeout(() => {
        if (!settled) {
          try {
            ws.close();
          } catch {
            /* noop */
          }
          done(false);
        }
      }, timeoutMs);

      ws.onerror = () => {
        clearTimeout(timer);
        done(false);
      };
      ws.onclose = () => {
        this.connected = false;
        clearTimeout(timer);
        done(false);
        this.onClose?.();
      };
      ws.onmessage = (ev) => {
        let msg: ServerMsg;
        try {
          msg = JSON.parse(String(ev.data)) as ServerMsg;
        } catch {
          return;
        }
        switch (msg.t) {
          case 'welcome':
            this.playerId = msg.playerId;
            this.seed = msg.seed;
            this.connected = true;
            clearTimeout(timer);
            done(true);
            break;
          case 'snapshot':
            this.onSnapshot?.({
              tick: msg.tick,
              you: msg.you,
              world: msg.world,
              ents: msg.ents,
              parcels: msg.parcels,
            });
            break;
          case 'feed':
            this.onFeed?.(msg.msgs);
            break;
          case 'season':
            this.onSeason?.(msg.result);
            break;
          default:
            break;
        }
      };
    });
  }

  private send(m: ClientMsg): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  hello(name: string, token?: string): void {
    this.send({ t: 'hello', name, ...(token ? { token } : {}) });
  }

  intent(intent: Intent): void {
    this.send({ t: 'intent', intent });
  }

  /** 뷰포트 부분 구독 — 카메라가 보는 원만 구독한다. */
  viewport(x: number, z: number, r: number): void {
    this.send({ t: 'viewport', x, z, r });
  }

  close(): void {
    this.ws?.close();
  }
}
